import usePromoSource from '@/hooks/usePromoSource';
import { hasWindow, yieldToMain } from '@/utils/dom';
import { useContext, useEffect } from 'react';
import { flushCachedEvents } from './events';
import { HistoryContext } from '@/history/historyProvider';
import { country } from '@/constants/country';

export type PageTypes =
	| 'home'
	| 'products'
	| 'revolving-loan'
	| 'deferred-payment'
	| 'money-transfer'
	| 'investment'
	| 'insurance'
	| 'packages'
	| 'card'
	| 'contact'
	| 'rewards'
	| 'documents'
	| 'personalData'
	| 'personalDataRequests'
	| 'termsAndConditions'
	| 'pay-in-three'
	| 'postpone-due-date'
	| 'pricing'
	| 'for-journalists'
	| 'faq'
	| 'faq-category'
	| 'faq-detail'
	| 'forEshops'
	| 'forDevelopers'
	| 'where-to-shop'
	| 'exchange-rate'
	| 'about-payment-on-delivery'
	| 'about-us'
	| 'career'
	| 'career-jobs-list'
	| 'career-detail'
	| 'seo-payment-online'
	| 'press-releases'
	| 'press-release-detail'
	| 'wrote-about-us'
	| 'seo-about-payment-on-delivery'
	| 'how-to-pay'
	| 'openapi'
	| 'questionnaire'
	| 'shop-wisely'
	| 'mallpay'
	| 'seo-credit-card'
	| 'seo-overdraft'
	| 'extended-warranty'
	| 'seo-deferred-payment'
	| 'card-payment'
	| 'seo-card-payment'
	| 'seo-buy-on-installments'
	| 'mobile-app'
	| 'app-store-redirect'
	| 'seo-loans'
	| 'gcss-homepage'
	| 'gcss-homepage-search'
	| 'gcss-category'
	| 'gcss-subcategory'
	| 'testimonials'
	| 'discounts'
	| 'discounts-category'
	| 'referral'
	| 'welcome-video'
	| 'privacy-settings'
	| 'retention'
	| 'seo-payment-gateway'
	| 'whistleblowing'
	| 'marketingConsents'
	| 'otherDocuments'
	| 'informationMemorandum'
	| 'xmas'
	| 'spring-offer'
	| 'deprecatedInsurance'
	| 'deprecatedInsuranceToS'
	| 'installments'
	| 'partners'
	| 'overpay-refund'
	| 'loan'
	| 'insuranceAndWarranty'
	| 'insurance-report';

type Params = Record<'title' | 'url', string> & Partial<Record<'level', string>>;

function logPageView(
	path: PageTypes,
	promoSource: string | null,
	previousRoute: string,
	params?: Params
) {
	if (!hasWindow()) {
		return;
	}

	const { pathname, search, hostname } = window.location;

	const page_location = search ? pathname + search : pathname;

	yieldToMain(() => {
		if (!window.dataLayer) {
			window.dataLayer = [];
		}

		window.dataLayer.push({
			event: 'page_view',
			page_hostname: hostname,
			page_location,
			page_title: path,
			web_section: 'static',
			country,
			...(promoSource !== null && { visit_source: promoSource }),
			...(previousRoute !== '' && { page_referrer: previousRoute }),
			...params,
		});

		flushCachedEvents();
	});
}

export function usePageView(path: PageTypes, params?: Params) {
	const promoSource = usePromoSource();
	const { previousRoute } = useContext(HistoryContext);

	useEffect(() => {
		logPageView(path, promoSource, previousRoute, params);
	}, [params, path, previousRoute, promoSource]);
}
